import React, { useEffect, useMemo } from 'react'
import { sbEditable, SbEditableBlok } from '@storyblok/storyblok-editable'
import { DynamicComponent } from '../components/DynamicComponent'
import { Seo } from '../components/Seo'
import { DefaultLayout } from '../layouts/DefaultLayout'
import { ThemeName } from '../lib/constants'
import { useStoryblok } from '../lib/storyblok'
import { useCurrentLanguageKey } from '../lib/context/i18n'
import { HotjarEvent, useTriggerHotjarSurvey } from '../lib/hooks/useTriggerHotjarSurvey'
import useEventTracking from '../lib/hooks/tracking'
import { useStoryblokDraft, StoryblokDraftPreview } from '../components/StoryblokDraftPreview'
import styled from 'styled-components'
import { Button } from '../components/UI'
import CustomLink from '../components/CustomLink'
import { typoHeadingXl, typoParagraphIntro } from '../lib/styles/Typography'
import { getAnnouncementBannerFromContent } from '../components/UI/AnnouncementBanner/types'

const Content = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  margin-top: 80px;
`

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 60px;
  padding-left: 20px;
  padding-right: 20px;
`

const TextsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;
`

const Title = styled.h1`
  ${typoHeadingXl}
  text-align: center;
`

const Subtitle = styled.p`
  ${typoParagraphIntro}
  text-align: center;
`

const Image = styled.img`
  width: 123.484px;
  height: 120px;
`

const ErrorPageTemplate = ({ pageContext }: { pageContext: SbEditableBlok & { translatedContent?: Record<string, any> } }) => {
  const { story, content: defaultContent } = useStoryblok(pageContext)
  const currentLanguage = useCurrentLanguageKey()
  const trackEvent = useEventTracking()

  useTriggerHotjarSurvey(HotjarEvent.USER_PERSONA_SURVEY)

  const { story: draftStory, loading: draftLoading } = useStoryblokDraft()

  // Use translated content if available, otherwise fall back to default
  const content = useMemo(() => {
    const translatedContent = pageContext.translatedContent
    if (translatedContent && translatedContent[currentLanguage]) {
      return translatedContent[currentLanguage]
    }
    return defaultContent
  }, [currentLanguage, pageContext.translatedContent, defaultContent])

  useEffect(() => {
    // TODO: remove this once we have confirmed 404 tracking is working as expected and we've removed /error-page page
    //       right now we want to make sure only /404 are tracked and since we have both /404 and /error-page we need to filter here
    if (pageContext.is404Page === true) {
      trackEvent('page_not_found', {
        url: window.location.href,
        pathname: window.location.pathname,
        referrer: document.referrer || '(direct)',
        search_params: window.location.search || undefined,
        hash: window.location.hash || undefined,
        timestamp: new Date().toISOString(),
      })
    }
  }, [pageContext.is404Page])

  if (draftStory) {
    return <StoryblokDraftPreview story={draftStory} />
  }

  if (draftLoading) {
    return (
      <DefaultLayout>
        <Content>
          <ContentContainer>
            <TextsContainer>
              <Title>Page not found</Title>
              <Subtitle>Looking for draft content...</Subtitle>
            </TextsContainer>
          </ContentContainer>
        </Content>
      </DefaultLayout>
    )
  }

  const components = content?.body?.map(blok => {
    return <DynamicComponent blok={blok} key={blok._uid} />
  })

  const image = content?.image

  return (
    <DefaultLayout
      theme={content?.color_theme ? (`theme-${content.color_theme}` as ThemeName) : undefined}
      announcementBanner={getAnnouncementBannerFromContent(content)}
    >
      <Seo
        title={pageContext?.field_pageTitle_string || pageContext?.name}
        description={pageContext?.field_pageDescription_string}
        lang={currentLanguage}
        fullSlug={pageContext?.full_slug}
        noIndex
        translatedSlugs={pageContext?.translated_slugs}
        defaultFullSlug={pageContext?.default_full_slug}
      />
      <Content>
        <ContentContainer>
          {image?.filename && (
            <Image src={image.filename as string} alt={content?.Title || 'Error'} />
          )}
          <TextsContainer>
            <Title>{content?.Title}</Title>
            <Subtitle>{content?.Subtitle}</Subtitle>
          </TextsContainer>
          <Button as={CustomLink} to="/" $ignoreTheme>
            {content?.buttonLabel}
          </Button>
        </ContentContainer>
      </Content>

      <div {...sbEditable(story)}>{components}</div>
    </DefaultLayout>
  )
}

export default ErrorPageTemplate
